"use client";

import React from "react";
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import Link from "next/link";
import Image from "next/image";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { useMutation } from "@tanstack/react-query";

const formSchema = z.object({
    email: z.string().email({ message: "Inserisci un indirizzo email valido." }),
});

type FormData = z.infer<typeof formSchema>;

export default function RecuperaPasswordClient() {
    const form = useForm<FormData>({
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
        defaultValues: { email: '' },
    });

    const sendMutation = useMutation({
        mutationFn: async (data: FormData) => {
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/auth/send-reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: data.email }),
            });

            const result = await response.json();

            if (result.status !== 'success') {
                throw new Error(result.message || 'Errore durante l\'invio. Riprova.');
            }

            return result;
        },
    });

    const onSubmit = (data: FormData) => {
        sendMutation.mutate(data);
    };

    if (sendMutation.isSuccess) {
        return (
            <div className="bg-muted flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
                <div className="flex w-full max-w-sm flex-col gap-6">
                    <Link href="/" className="flex items-center gap-2 self-center font-medium">
                        <Image
                            src="/logo-vettoriale.svg"
                            alt="Gruppo Piccirillo logo"
                            width={70}
                            height={70}
                            priority={true}
                            className="h-10 w-auto"
                        />
                    </Link>

                    <Card>
                        <CardHeader className="text-center">
                            <div className="flex justify-center mb-4">
                                <CheckCircle className="h-16 w-16 text-green-600" />
                            </div>
                            <CardTitle className="text-xl">Email inviata!</CardTitle>
                            <CardDescription>
                                Se l&apos;indirizzo è registrato, riceverai a breve un&apos;email con le
                                istruzioni per reimpostare la password.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-800 space-y-1">
                                <p>Controlla la tua casella di posta (anche la cartella spam).</p>
                                <p>Il codice OTP nel link è valido per <strong>30 minuti</strong>.</p>
                            </div>
                            <div className="text-center text-sm">
                                <Link
                                    href="/auth/accedi"
                                    className="underline underline-offset-4 text-[#001AFF] hover:text-[#000D7D]"
                                >
                                    Torna al login
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-muted flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
            <div className="flex w-full max-w-sm flex-col gap-6">
                <Link href="/" className="flex items-center gap-2 self-center font-medium">
                    <Image
                        src="/logo-vettoriale.svg"
                        alt="Gruppo Piccirillo logo"
                        width={70}
                        height={70}
                        priority={true}
                        className="h-10 w-auto"
                    />
                </Link>

                <div className={cn("flex flex-col gap-6")}>
                    <Card>
                        <CardHeader className="text-center">
                            <div className="flex justify-center mb-2">
                                <Mail className="h-10 w-10 text-[#000D7D]" />
                            </div>
                            <CardTitle className="text-xl">Password dimenticata?</CardTitle>
                            <CardDescription>
                                Inserisci la tua email e ti invieremo un codice per reimpostare la password.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
                                    <FormField
                                        control={form.control}
                                        name="email"
                                        render={({ field, fieldState }) => (
                                            <FormItem>
                                                <div className="grid gap-3">
                                                    <Label htmlFor="email">Email</Label>
                                                    <FormControl>
                                                        <Input
                                                            {...field}
                                                            id="email"
                                                            type="email"
                                                            placeholder="m@example.com"
                                                            autoComplete="email"
                                                        />
                                                    </FormControl>
                                                </div>
                                                <FormMessage>{fieldState.error?.message}</FormMessage>
                                            </FormItem>
                                        )}
                                    />

                                    {sendMutation.isError && (
                                        <div className="text-sm text-red-600 text-center p-3 bg-red-50 rounded-md border border-red-200">
                                            {sendMutation.error?.message}
                                        </div>
                                    )}

                                    <Button
                                        type="submit"
                                        className="text-white w-full bg-[#000D7D]"
                                        disabled={sendMutation.isPending}
                                    >
                                        {sendMutation.isPending ? "Invio in corso..." : "Invia istruzioni"}
                                    </Button>

                                    <div className="text-center text-sm">
                                        <Link
                                            href="/auth/accedi"
                                            className="inline-flex items-center gap-1 underline underline-offset-4 text-[#001AFF] hover:text-[#000D7D]"
                                        >
                                            <ArrowLeft className="h-3 w-3" />
                                            Torna al login
                                        </Link>
                                    </div>
                                </form>
                            </Form>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
